import {focusSource} from '@wix/music-shared/dist/src/accessibility/focus-source';
import {focusWithin} from '@wix/music-shared/dist/src/accessibility/focus-within';
import {maintainDisabled} from '@wix/music-shared/dist/src/accessibility/maintain-disabled';
import {maintainTabFocus} from '@wix/music-shared/dist/src/accessibility/maintain-tab-focus';
import {ESC_KEY} from '@wix/music-shared/dist/src/utils/keyboard/key-codes';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {BackButtonContainer} from '../../containers/BackButtonContainer';
import {closePage} from '../../services';
import {getLocale} from '../../services/env/locale/locale';
import {useBiLogger} from '../BiProvider/withBiLogger';
import {ClosePageButton} from '../ClosePageButton/ClosePageButton';
import {GoToSiteButton} from '../GoToSiteButton/GoToSiteButton';
import {CLOSE_BUTTONS_CONSTANT} from '../Page/CloseButtonsConstant';
import styles from './PageFrame.scss';

interface IProps {
  documentTitle: string;
  className: string;
  hasPaymentError?: boolean;
  showBackButton?: boolean;
  children: React.ReactNode;
}

export const PageFrame: React.FunctionComponent<IProps> = ({
  className,
  children,
  hasPaymentError,
  showBackButton = true,
  documentTitle,
}) => {
  const {t} = useTranslation();
  const biLogger = useBiLogger();
  const element = useRef<HTMLDivElement>(null);

  const handleSiteClose = (buttonName) => {
    closePage(biLogger, buttonName);
  };

  const handleCloseWindowWithEscape = (event) => {
    if (event.keyCode === ESC_KEY) {
      handleSiteClose(CLOSE_BUTTONS_CONSTANT.ESCAPE_KEY);
    }
  };

  useEffect(() => {
    if (documentTitle) {
      document.title = documentTitle;
    }
  }, [documentTitle]);

  useEffect(() => {
    if (element.current) {
      element.current.focus();
    }

    document.body.addEventListener('keydown', handleCloseWindowWithEscape);

    const focusWithinDisengage = focusWithin();
    const handleFocusSource = focusSource();
    const handleTabFocus = maintainTabFocus({
      context: element.current,
    });
    document.documentElement.lang = getLocale();

    return () => {
      document.body.removeEventListener('keydown', handleCloseWindowWithEscape);
      focusWithinDisengage();
      handleFocusSource.disengage();
      handleTabFocus.disengage();
    };
  }, []);

  return (
    <div
      className={classNames(styles.pageFrame, {[styles.hasPaymentError]: hasPaymentError}, className)}
      role="dialog"
      tabIndex={-1}
      ref={element}
      data-hook="page-frame"
    >
      <ClosePageButton
        className={styles.closePageButton}
        iconClassName={styles.closePageButtonIcon}
        accName={t('mgw.aria.closePageButton')}
        onClick={() => handleSiteClose(CLOSE_BUTTONS_CONSTANT.CLOSE_PAGE_BUTTON)}
      />
      {children}
      <GoToSiteButton
        className={styles.goToSiteButton}
        onClick={() => handleSiteClose(CLOSE_BUTTONS_CONSTANT.GO_TO_SITE_BUTTON)}
      >
        {t('mgw.goToSiteButton')}
      </GoToSiteButton>
      {showBackButton && <BackButtonContainer />}
    </div>
  );
};
PageFrame.displayName = 'PageFrame';
//
// PageFrame.propTypes = {
//   hasPaymentError: PropTypes.bool,
//   biLogger: PropTypes.object,
//   children: PropTypes.node,
//   documentTitle: PropTypes.string,
//   className: PropTypes.string,
// };
